/**
 * BP-026 · Platform Dashboard — cross-tenant KPIs + valuation, funnel,
 *          time-series, and per-tenant AI cost.
 */
import { useEffect, useState } from "react";
import {
  Activity, Building2, DollarSign, Users, Sparkles,
  TrendingUp, Users2, Coins, Trophy, LineChart as LineChartIcon,
  Settings2, Camera, X, Loader2, Grid3x3, AlertTriangle, LayoutList,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend, BarChart, Bar,
} from "recharts";
import { apiGet, apiPut, apiPost } from "@/lib/api";

const fmtUsd = (n) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
  : n >= 1_000     ? `$${(n / 1_000).toFixed(1)}k`
  : `$${(n ?? 0).toLocaleString()}`;

const KpiTile = ({ icon: Icon, label, value, sub, tid, tone = "default" }) => {
  const iconTone =
    tone === "valuation"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : "bg-[var(--primary)]/10 text-[var(--primary)] border-[var(--primary)]/20";
  return (
    <div data-testid={tid}
         className="relative rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_6px_18px_rgba(15,23,42,0.08)] hover:-translate-y-0.5 transition-all">
      <div className="flex items-start justify-between gap-3">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-2)]">{label}</div>
        <div className={`w-8 h-8 rounded-lg border flex items-center justify-center ${iconTone}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="mt-3 font-display text-3xl font-black text-[var(--ink)] tracking-tight">{value}</div>
      {sub && <div className="text-[11px] text-[var(--ink-2)] mt-1.5 leading-relaxed">{sub}</div>}
    </div>
  );
};

const Card = ({ title, kicker, icon: Icon, tid, children, right }) => (
  <div data-testid={tid}
       className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--primary)]">
          {kicker}
        </div>
        <div className="mt-1 flex items-center gap-2 font-display text-lg font-black text-[var(--ink)]">
          {Icon && <Icon className="w-4 h-4 text-[var(--primary)]" />}
          {title}
        </div>
      </div>
      {right}
    </div>
    <div className="mt-4">{children}</div>
  </div>
);

// ─────────────────────── Valuation section ───────────────────────

function EditMultiplesModal({ open, onClose, current, onSaved }) {
  const [bear, setBear] = useState(current?.bear ?? 4);
  const [base, setBase] = useState(current?.base ?? 8);
  const [bull, setBull] = useState(current?.bull ?? 14);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (open && current) {
      setBear(current.bear); setBase(current.base); setBull(current.bull);
      setErr(null);
    }
  }, [open, current]);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!(Number(bear) <= Number(base) && Number(base) <= Number(bull))) {
      setErr("Must satisfy bear ≤ base ≤ bull.");
      return;
    }
    setSaving(true);
    try {
      const res = await apiPut("/api/v3/platform/valuation/settings", {
        bear: Number(bear), base: Number(base), bull: Number(bull),
      });
      onSaved(res.multiples);
      onClose();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      data-testid="valuation-edit-modal"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-6"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl border border-slate-300 bg-white p-6"
      >
        <div className="flex items-center justify-between">
          <div className="font-bold text-slate-900 text-lg">Edit valuation multiples</div>
          <button type="button" onClick={onClose}
                  data-testid="valuation-edit-close"
                  className="text-slate-500 hover:text-slate-900">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="mt-2 text-sm text-slate-600">
          ARR × multiple for each scenario. Constraint: bear ≤ base ≤ bull.
        </p>
        <div className="mt-5 space-y-4">
          {[
            { key: "bear", val: bear, set: setBear, color: "text-red-600" },
            { key: "base", val: base, set: setBase, color: "text-emerald-600" },
            { key: "bull", val: bull, set: setBull, color: "text-sky-600" },
          ].map(({ key, val, set, color }) => (
            <label key={key} className="block">
              <span className={`text-xs uppercase font-bold ${color}`}>{key}</span>
              <input
                type="number" step="0.1" min="0.1" max="100"
                required
                value={val}
                data-testid={`valuation-input-${key}`}
                onChange={(e) => set(e.target.value)}
                className="mt-1 w-full rounded-md bg-slate-50 border border-slate-300 text-slate-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </label>
          ))}
        </div>
        {err && (
          <div data-testid="valuation-edit-error"
               className="mt-3 rounded bg-red-500/10 border border-red-500/30 text-red-600 text-sm px-3 py-2">
            {err}
          </div>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button" onClick={onClose}
            className="px-4 py-2 rounded-md text-slate-700 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="valuation-edit-save"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

function ValuationSection({ v, settings, history, onReload }) {
  const [editing, setEditing] = useState(false);
  const [snapping, setSnapping] = useState(false);
  if (!v) return null;
  const bear = v.scenarios?.find((s) => s.key === "bear");
  const base = v.scenarios?.find((s) => s.key === "base");
  const bull = v.scenarios?.find((s) => s.key === "bull");

  const trajectory = (history || []).map((h) => ({
    date: h.captured_at.slice(5, 10),
    base_valuation: (h.scenarios?.find((s) => s.key === "base") || {}).valuation ?? 0,
  }));

  const doSnapshot = async () => {
    setSnapping(true);
    try {
      await apiPost("/api/v3/platform/valuation/snapshot", { note: "" });
      onReload();
    } finally {
      setSnapping(false);
    }
  };

  return (
    <>
      <Card
        title="Company Valuation"
        kicker="ARR × multiple"
        icon={Trophy}
        tid="section-valuation"
        right={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setEditing(true)}
              data-testid="valuation-edit-btn"
              className="inline-flex items-center gap-1 text-xs font-semibold rounded-md border border-slate-300 hover:border-emerald-500 hover:text-emerald-600 text-slate-700 px-3 py-1.5"
            >
              <Settings2 className="w-3.5 h-3.5" /> Edit multiples
            </button>
            <button
              type="button"
              onClick={doSnapshot}
              disabled={snapping}
              data-testid="valuation-snapshot-btn"
              className="inline-flex items-center gap-1 text-xs font-semibold rounded-md bg-emerald-50  hover:bg-emerald-100     border border-emerald-300      text-emerald-700 px-3 py-1.5 disabled:opacity-60"
            >
              {snapping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
              Snapshot now
            </button>
          </div>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-2)]">ARR</div>
            <div className="mt-2 font-display text-2xl font-black text-[var(--ink)]" data-testid="valuation-arr">
              {fmtUsd(v.arr)}
            </div>
            <div className="text-[11px] text-[var(--ink-2)] mt-1">
              MRR {fmtUsd(v.mrr)} · {v.subscribers_active} subs
            </div>
          </div>
          {[
            { key: "bear",  label: "Bear",  scenario: bear,
              tone: "text-rose-700",    ring: "border-rose-200 bg-rose-50/50 hover:border-rose-300"    },
            { key: "base",  label: "Base",  scenario: base,
              tone: "text-amber-800",   ring: "border-[var(--primary)]/30 bg-[var(--primary)]/[0.05] hover:border-[var(--primary)]/50" },
            { key: "bull",  label: "Bull",  scenario: bull,
              tone: "text-sky-700",     ring: "border-sky-200 bg-sky-50/50 hover:border-sky-300"      },
          ].map(({ key, label, scenario, tone, ring }) => (
            <div
              key={key}
              data-testid={`valuation-scenario-${key}`}
              className={`relative rounded-2xl border p-4 transition-colors ${ring}`}
            >
              <div className={`text-[10px] uppercase tracking-widest font-bold ${tone}`}>
                {label} · {scenario?.multiple}× ARR
              </div>
              <div className={`mt-2 font-display text-2xl font-black ${tone}`}>
                {fmtUsd(scenario?.valuation ?? 0)}
              </div>
              <div className="text-[10px] text-[var(--ink-2)] mt-1">
                {key === "bear" && "Discounted · slow growth"}
                {key === "base" && "Median SaaS · moderate growth"}
                {key === "bull" && "High-growth · elite retention"}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-2)]">Growth 90d</div>
            <div className={`font-mono font-bold mt-0.5 ${v.growth_pct_90d >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
              {v.growth_pct_90d >= 0 ? "+" : ""}{v.growth_pct_90d}%
            </div>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-2)]">Churn 90d</div>
            <div className="font-mono font-bold text-[var(--ink)] mt-0.5">{v.churn_pct_90d}%</div>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-2)]">Net retention</div>
            <div className="font-mono font-bold text-[var(--ink)] mt-0.5">{v.net_retention_pct}%</div>
          </div>
        </div>

        {/* Trajectory sparkline */}
        {trajectory.length > 0 && (
          <div className="mt-5 pt-4 border-t border-slate-200" data-testid="valuation-trajectory">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-2)]">
                Base-valuation trajectory · {trajectory.length} snapshots
              </div>
              <div className="text-[10px] font-mono text-[var(--ink-2)]">
                Latest {fmtUsd(trajectory[trajectory.length - 1]?.base_valuation ?? 0)}
              </div>
            </div>
            <div style={{ width: "100%", height: 110 }} className="mt-2">
              <ResponsiveContainer>
                <LineChart data={trajectory} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#475569" fontSize={10} minTickGap={20} />
                  <YAxis stroke="#10b981" fontSize={10} tickFormatter={(v_) => fmtUsd(v_)} />
                  <Tooltip
                    contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0",
                                    borderRadius: 8, fontSize: 12 }}
                    formatter={(val) => fmtUsd(val)}
                  />
                  <Line type="monotone" dataKey="base_valuation"
                        stroke="#10b981" strokeWidth={2}
                        dot={{ r: 2, fill: "#10b981" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {settings?.updated_at && (
          <div className="mt-3 text-[10px] text-slate-500">
            Multiples last updated {settings.updated_at.slice(0, 10)} by {settings.updated_by?.email}
          </div>
        )}
      </Card>

      <EditMultiplesModal
        open={editing}
        onClose={() => setEditing(false)}
        current={settings?.multiples}
        onSaved={() => onReload()}
      />
    </>
  );
}

// ─────────────────────── Conversion funnel ───────────────────────

function FunnelSection({ funnel }) {
  if (!funnel) return null;
  const top = funnel.stages?.[0]?.count || 1;
  return (
    <Card
      title="Trial → Paid Funnel"
      kicker="Last 90d"
      icon={TrendingUp}
      tid="section-funnel"
    >
      <div className="space-y-3">
        {funnel.stages.map((s) => {
          const pct = (s.count / top) * 100;
          return (
            <div key={s.key} data-testid={`funnel-stage-${s.key}`}>
              <div className="flex items-baseline justify-between text-xs">
                <span className="text-slate-700 font-semibold">{s.label}</span>
                <span className="text-slate-500 font-mono">
                  {s.count.toLocaleString()}
                  {s.pct_of_prior != null && (
                    <span className="ml-2 text-emerald-600">
                      {s.pct_of_prior}% of prior
                    </span>
                  )}
                </span>
              </div>
              <div className="mt-1 h-6 bg-slate-50 rounded overflow-hidden border border-slate-200">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400"
                  style={{ width: `${Math.max(3, pct)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
        <div className="rounded bg-slate-100 px-3 py-2">
          <div className="text-slate-500 uppercase text-[10px]">Signup → Trial</div>
          <div className="font-mono font-bold text-emerald-600">
            {funnel.totals.signup_to_trial_pct}%
          </div>
        </div>
        <div className="rounded bg-slate-100 px-3 py-2">
          <div className="text-slate-500 uppercase text-[10px]">Trial → Paid</div>
          <div className="font-mono font-bold text-emerald-600">
            {funnel.totals.trial_to_paid_pct}%
          </div>
        </div>
        <div className="rounded bg-slate-100 px-3 py-2">
          <div className="text-slate-500 uppercase text-[10px]">Signup → Paid</div>
          <div className="font-mono font-bold text-emerald-600">
            {funnel.totals.signup_to_paid_pct}%
          </div>
        </div>
      </div>
    </Card>
  );
}

// ─────────────────────── Time series ───────────────────────

function TimeSeriesSection({ ts }) {
  if (!ts) return null;
  const data = (ts.series || []).map((r) => ({
    date: r.date.slice(5),  // MM-DD
    MRR: r.mrr,
    "Active users": r.active_users,
  }));
  return (
    <Card
      title="MRR + Active Users"
      kicker="Last 90d · daily"
      icon={LineChartIcon}
      tid="section-timeseries"
    >
      <div style={{ width: "100%", height: 260 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" stroke="#475569" fontSize={10} minTickGap={20} />
            <YAxis yAxisId="mrr" stroke="#10b981" fontSize={10}
                   tickFormatter={(v) => fmtUsd(v)} />
            <YAxis yAxisId="users" orientation="right" stroke="#38bdf8" fontSize={10} />
            <Tooltip
              contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0",
                              borderRadius: 8, fontSize: 12 }}
              labelStyle={{ color: "#334155" }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line yAxisId="mrr"   type="monotone" dataKey="MRR"
                  stroke="#10b981" strokeWidth={2} dot={false} />
            <Line yAxisId="users" type="monotone" dataKey="Active users"
                  stroke="#38bdf8" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

// ─────────────────────── AI cost by tenant ───────────────────────

function AiCostSection({ cost }) {
  if (!cost) return null;
  const top = cost.tenants?.[0]?.total_cost || 1;
  return (
    <Card
      title="AI Cost by Tenant"
      kicker={`Last 30d · $${cost.total_cost_usd?.toFixed(2)} platform-wide`}
      icon={Coins}
      tid="section-ai-cost"
    >
      {cost.tenants.length === 0 ? (
        <div className="text-sm text-slate-500">No AI usage in window.</div>
      ) : (
        <div className="space-y-2">
          {cost.tenants.slice(0, 10).map((t) => {
            const pct = (t.total_cost / top) * 100;
            return (
              <div
                key={t.workspace_id}
                data-testid={`ai-cost-row-${t.workspace_id}`}
              >
                <div className="flex items-baseline justify-between text-xs">
                  <span className="text-slate-700 font-semibold truncate max-w-xs">
                    {t.tenant_name}
                  </span>
                  <span className="text-slate-500 font-mono">
                    ${t.total_cost.toFixed(2)} · {t.total_count} calls
                  </span>
                </div>
                <div className="mt-1 h-3 bg-slate-50 rounded overflow-hidden border border-slate-200">
                  <div
                    className="h-full bg-gradient-to-r from-amber-500 to-amber-400"
                    style={{ width: `${Math.max(2, pct)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ─────────────────────── Retention cohorts ───────────────────────

function RetentionCohortsSection({ rc }) {
  if (!rc) return null;
  return (
    <Card
      title="Retention Cohorts"
      kicker={`${rc.weeks || 12}-week window · D1 / D7 / D30`}
      icon={LayoutList}
      tid="section-retention"
      right={
        <div className="text-xs text-slate-600">
          Avg: D1 <span className="text-emerald-600 font-mono font-bold">{rc.average?.d1_pct}%</span> ·
          D7 <span className="text-emerald-600 font-mono font-bold">{rc.average?.d7_pct}%</span> ·
          D30 <span className="text-emerald-600 font-mono font-bold">{rc.average?.d30_pct}%</span>
        </div>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-slate-500 uppercase">
            <tr>
              <th className="text-left px-2 py-2">Cohort week</th>
              <th className="text-right px-2 py-2">Signups</th>
              <th className="text-right px-2 py-2">D1</th>
              <th className="text-right px-2 py-2">D7</th>
              <th className="text-right px-2 py-2">D30</th>
            </tr>
          </thead>
          <tbody>
            {(rc.cohorts || []).length === 0 ? (
              <tr><td colSpan="5" className="px-2 py-4 text-center text-slate-500">
                No cohorts in window.
              </td></tr>
            ) : rc.cohorts.map((r) => (
              <tr key={r.cohort} data-testid={`retention-row-${r.cohort}`}
                  className="border-t border-slate-200 hover:bg-slate-100">
                <td className="px-2 py-2 font-mono text-slate-700">{r.cohort}</td>
                <td className="px-2 py-2 text-right text-slate-900">{r.signups}</td>
                {[r.d1_pct, r.d7_pct, r.d30_pct].map((p, i) => (
                  <td key={i} className="px-2 py-2 text-right font-mono"
                      style={{ color: p >= 40 ? "#10b981" : p >= 15 ? "#f59e0b" : "#94a3b8" }}>
                    {p}%
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─────────────────────── Feature adoption funnel ───────────────────────

function FeatureAdoptionSection({ fa }) {
  if (!fa) return null;
  return (
    <Card
      title="Feature Adoption"
      kicker={`${fa.total_tenants} tenants · % using each Build Pack`}
      icon={Grid3x3}
      tid="section-feature-adoption"
    >
      <div className="space-y-2">
        {(fa.features || []).map((f) => (
          <div key={f.bp} data-testid={`adoption-row-${f.bp}`}>
            <div className="flex items-baseline justify-between text-xs">
              <span className="text-slate-700">
                <span className="font-mono text-[10px] text-emerald-500 mr-2">{f.bp}</span>
                {f.name}
              </span>
              <span className="text-slate-500 font-mono">
                {f.adopted_tenants} · {f.adoption_pct}%
              </span>
            </div>
            <div className="mt-1 h-3 bg-slate-50 rounded overflow-hidden border border-slate-200">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400"
                style={{ width: `${Math.max(1, f.adoption_pct)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─────────────────────── Billing failures funnel ───────────────────────

function BillingFailuresSection({ bf }) {
  if (!bf) return null;
  const top = Math.max(1, ...bf.stages.map((s) => s.count));
  return (
    <Card
      title="Billing Failures & Dunning"
      kicker={`Last ${bf.window_days}d`}
      icon={AlertTriangle}
      tid="section-billing-failures"
    >
      <div className="space-y-3">
        {bf.stages.map((s) => {
          const pct = (s.count / top) * 100;
          const color = s.key === "active" ? "from-emerald-500 to-emerald-400"
                      : s.key === "canceled" ? "from-red-500 to-red-400"
                      : "from-amber-500 to-amber-400";
          return (
            <div key={s.key} data-testid={`billing-stage-${s.key}`}>
              <div className="flex items-baseline justify-between text-xs">
                <span className="text-slate-700 font-semibold">{s.label}</span>
                <span className="text-slate-500 font-mono">
                  {s.count} · {s.pct}%
                </span>
              </div>
              <div className="mt-1 h-5 bg-slate-50 rounded overflow-hidden border border-slate-200">
                <div
                  className={`h-full bg-gradient-to-r ${color}`}
                  style={{ width: `${Math.max(2, pct)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        <div className="rounded bg-slate-100 px-3 py-2">
          <div className="text-slate-500 uppercase text-[10px]">Recovery rate</div>
          <div className="font-mono font-bold text-emerald-600">
            {bf.totals?.recovery_rate_pct}%
          </div>
        </div>
        <div className="rounded bg-slate-100 px-3 py-2">
          <div className="text-slate-500 uppercase text-[10px]">Involuntary churn</div>
          <div className="font-mono font-bold text-red-600">
            {bf.totals?.involuntary_churn_pct}%
          </div>
        </div>
      </div>
    </Card>
  );
}

// ─────────────────────── AI cost metered (enhanced) ───────────────────────

function AiCostMeteredSection({ metered }) {
  if (!metered) return null;
  const top = metered.tenants?.[0]?.total_cost || 1;
  return (
    <Card
      title="AI Spend by Tenant · Metered"
      kicker={`Last ${metered.window_days}d · $${metered.total_cost_usd?.toFixed(2)} platform-wide`}
      icon={Coins}
      tid="section-ai-cost-metered"
      right={
        <div className="text-[10px] font-mono text-slate-500">
          Meter accuracy{" "}
          <span className={metered.meter_status?.accuracy_pct >= 80 ? "text-emerald-600" : "text-amber-600"}>
            {metered.meter_status?.accuracy_pct}%
          </span>
          {" · "}{metered.meter_status?.records_with_real_tokens}/
          {(metered.meter_status?.records_with_real_tokens ?? 0) +
           (metered.meter_status?.records_with_estimates ?? 0)} records with real tokens
        </div>
      }
    >
      {metered.tenants.length === 0 ? (
        <div className="text-sm text-slate-500">No AI usage in window.</div>
      ) : (
        <div className="space-y-2">
          {metered.tenants.slice(0, 10).map((t) => {
            const pct = (t.total_cost / top) * 100;
            const models = Object.keys(t.by_model || {}).slice(0, 3);
            return (
              <div key={t.workspace_id} data-testid={`ai-cost-metered-row-${t.workspace_id}`}>
                <div className="flex items-baseline justify-between text-xs">
                  <span className="text-slate-700 font-semibold truncate max-w-xs">
                    {t.tenant_name}
                  </span>
                  <span className="text-slate-500 font-mono">
                    ${t.total_cost.toFixed(4)} · {t.total_count} calls
                  </span>
                </div>
                <div className="mt-1 h-3 bg-slate-50 rounded overflow-hidden border border-slate-200">
                  <div className="h-full bg-gradient-to-r from-amber-500 to-amber-400"
                       style={{ width: `${Math.max(2, pct)}%` }} />
                </div>
                {models.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {models.map((m) => (
                      <span key={m} className="text-[9px] font-mono text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
                        {m}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ─────────────────────── Root ───────────────────────

export default function PlatformDashboard() {
  const [d, setD] = useState(null);
  const [valuationData, setValuation] = useState(null);
  const [valuationSettings, setValuationSettings] = useState(null);
  const [valuationHistory, setValuationHistory] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [ts, setTs] = useState(null);
  const [cost, setCost] = useState(null);
  const [retention, setRetention] = useState(null);
  const [adoption, setAdoption] = useState(null);
  const [billingFail, setBillingFail] = useState(null);
  const [metered, setMetered] = useState(null);

  const reloadValuation = () => {
    apiGet("/api/v3/platform/valuation").then(setValuation).catch(() => {});
    apiGet("/api/v3/platform/valuation/settings").then(setValuationSettings).catch(() => {});
    apiGet("/api/v3/platform/valuation/history?limit=40")
      .then((r) => setValuationHistory(r.snapshots || [])).catch(() => {});
  };

  useEffect(() => {
    apiGet("/api/v3/platform/dashboard?window_days=30").then(setD).catch(() => {});
    reloadValuation();
    apiGet("/api/v3/platform/conversion-funnel?window_days=90").then(setFunnel).catch(() => {});
    apiGet("/api/v3/platform/time-series?window_days=90").then(setTs).catch(() => {});
    apiGet("/api/v3/platform/ai-cost-by-tenant?window_days=30").then(setCost).catch(() => {});
    apiGet("/api/v3/platform/retention-cohorts?weeks=12").then(setRetention).catch(() => {});
    apiGet("/api/v3/platform/feature-adoption").then(setAdoption).catch(() => {});
    apiGet("/api/v3/platform/billing-failures?window_days=90").then(setBillingFail).catch(() => {});
    apiGet("/api/v3/platform/ai-cost-metered?window_days=30").then(setMetered).catch(() => {});
  }, []);

  if (!d)
    return (
      <div className="p-6 text-[var(--ink-2)]">Loading dashboard…</div>
    );

  return (
    <div data-testid="platform-dashboard" className="space-y-6">
      <header>
        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--primary)]">
          Cross-tenant · 30d window
        </div>
        <h1 className="mt-1 font-display text-3xl font-black text-[var(--ink)] tracking-tight">Platform Overview</h1>
        <p className="mt-1 text-sm text-[var(--ink-2)]">
          The mission-control view — every tenant, every dollar, every anomaly in one place.
        </p>
      </header>

      {/* Top KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiTile
          tid="kpi-tenants-total"
          icon={Building2}
          label="Tenants"
          value={d.tenants?.total ?? "—"}
          sub={`${d.tenants?.active} active · ${d.tenants?.suspended} suspended`}
        />
        <KpiTile
          tid="kpi-users-active"
          icon={Users}
          label="Active users"
          value={d.users?.active ?? "—"}
          sub={`of ${d.users?.total} total`}
        />
        <KpiTile
          tid="kpi-mrr"
          icon={DollarSign}
          label="Platform MRR"
          value={`$${(d.revenue?.mrr ?? 0).toLocaleString()}`}
          sub={`${d.revenue?.subscribers ?? 0} subscribers`}
        />
        <KpiTile
          tid="kpi-ai-usage"
          icon={Sparkles}
          label="AI narrations 30d"
          value={
            (d.ai_usage?.coach_narrations ?? 0) +
            (d.ai_usage?.market_advisors ?? 0)
          }
          sub="coach + market"
        />
      </div>

      {/* Latency + Valuation row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <ValuationSection
            v={valuationData}
            settings={valuationSettings}
            history={valuationHistory}
            onReload={reloadValuation}
          />
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 text-xs uppercase text-slate-500">
            <Activity className="w-4 h-4" />
            Latency
          </div>
          <div
            data-testid="dashboard-latency"
            className="mt-1 text-lg font-semibold text-emerald-600"
          >
            {d.latency_ms} ms
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            Spec target &lt; 250ms
          </div>
          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="text-xs uppercase text-slate-500 flex items-center gap-2">
              <Users2 className="w-3.5 h-3.5" />
              Retention snapshot
            </div>
            <div className="mt-2 text-sm">
              <div className="text-slate-600">Net retention</div>
              <div className="font-mono text-emerald-600 font-bold">
                {valuationData?.net_retention_pct ?? "—"}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Time series + Funnel row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TimeSeriesSection ts={ts} />
        <FunnelSection funnel={funnel} />
      </div>

      {/* AI cost table */}
      <AiCostSection cost={cost} />

      {/* AI cost metered (real per-model) */}
      <AiCostMeteredSection metered={metered} />

      {/* Retention cohorts + Feature adoption */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RetentionCohortsSection rc={retention} />
        <FeatureAdoptionSection fa={adoption} />
      </div>

      {/* Billing failures funnel */}
      <BillingFailuresSection bf={billingFail} />
    </div>
  );
}
